/**
 * Panel de Migraciones de Base de Datos
 * Permite ejecutar migraciones desde el frontend
 */

import React, { useState, useEffect } from 'react';
import { Database, Play, CheckCircle, AlertTriangle, RefreshCw, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MigracionesPanel = () => {
  const { token, user } = useAuth();
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const cargarEstado = async () => {
    setCargando(true);
    try {
      const res = await fetch(`${API_URL}/api/finanzas/migraciones/estado`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setEstado(data);
      }
    } catch (e) {
      console.error('Error cargando estado migraciones:', e);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (token && user?.rol === 'direccion') {
      cargarEstado();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.rol]);

  // Solo mostrar a dirección
  if (user?.rol !== 'direccion') {
    return null;
  }

  const ejecutarMigracion = async () => {
    if (!window.confirm('¿Estás seguro de ejecutar la migración? Esta acción convertirá todos los cargos a valores positivos.')) {
      return;
    }
    
    setEjecutando(true);
    setResultado(null);
    
    try {
      const res = await fetch(`${API_URL}/api/finanzas/migraciones/ejecutar-002`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setResultado({
          tipo: 'success',
          mensaje: data.mensaje,
          detalles: `Cargos: ${data.cargos_actualizados}, Facturas: ${data.facturas_actualizadas}`
        });
        // Recargar estado
        cargarEstado();
      } else {
        setResultado({
          tipo: 'error',
          mensaje: data.detail || 'Error ejecutando migración'
        });
      }
    } catch (e) {
      setResultado({
        tipo: 'error',
        mensaje: 'Error de conexión: ' + e.message
      });
    } finally {
      setEjecutando(false);
    }
  };

  if (!estado) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-4">
          <Database className="w-6 h-6 text-purple-600" />
          <h3 className="text-lg font-semibold">Migraciones de Base de Datos</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Cargando estado...</span>
        </div>
      </div>
    );
  }

  const mig002 = estado.migracion_002;

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Database className="w-6 h-6 text-purple-600" />
          <h3 className="text-lg font-semibold">Migraciones de Base de Datos</h3>
        </div>
        <button
          onClick={cargarEstado}
          disabled={cargando}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 ${cargando ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Migración 002 */}
      <div className={`border rounded-lg p-4 ${mig002.pendiente ? 'border-yellow-300 bg-yellow-50' : 'border-green-300 bg-green-50'}`}>
        <div className="flex items-start gap-3">
          {mig002.pendiente ? (
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
          ) : (
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
          )}
          
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">{mig002.nombre}</h4>
            <p className="text-sm text-gray-600 mt-1">{mig002.descripcion}</p>
            
            <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
              <div className="bg-white/50 rounded p-2">
                <div className="text-gray-500">Cargos negativos</div>
                <div className="font-bold text-lg">{mig002.cargos_negativos}</div>
              </div>
              <div className="bg-white/50 rounded p-2">
                <div className="text-gray-500">Facturas negativas</div>
                <div className="font-bold text-lg">{mig002.facturas_negativas}</div>
              </div>
              <div className="bg-white/50 rounded p-2">
                <div className="text-gray-500">Ya migrados</div>
                <div className="font-bold text-lg">{mig002.ya_migrados}</div>
              </div>
            </div>

            {mig002.pendiente && (
              <button
                onClick={ejecutarMigracion}
                disabled={ejecutando}
                className="mt-4 flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {ejecutando ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Ejecutando...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Ejecutar Migración
                  </>
                )}
              </button>
            )}

            {!mig002.pendiente && (
              <div className="mt-3 flex items-center gap-2 text-green-700">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Migración completada</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resultado */}
      {resultado && (
        <div className={`mt-4 p-4 rounded-lg ${
          resultado.tipo === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          <div className="font-medium">{resultado.mensaje}</div>
          {resultado.detalles && (
            <div className="text-sm mt-1">{resultado.detalles}</div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Lógica de cálculo:</strong> Los cargos bancarios se guardan en positivo y representan gastos. 
          El balance se calcula como: <code className="bg-blue-100 px-1 rounded">Ingresos - Gastos = Beneficio</code>
        </div>
      </div>
    </div>
  );
};

export default MigracionesPanel;
